import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { portalCandidateService } from '../api/portalCandidateService';
import { 
  getTheologicalSurvey,
  getReferences,
  getSendingChurch,
  submitTheologicalSurvey,
  submitReferences,
  submitSendingChurch
} from '../api/onboardingService';
import ProfileStep from './onboarding/ProfileStep';
import WhyChurchPlantingStep from './onboarding/WhyChurchPlantingStep';
import TheologyStep from './onboarding/TheologyStep';
import ReferencesStep from './onboarding/ReferencesStep';
import SendingChurchStep from './onboarding/SendingChurchStep';
import BackgroundCheckStep from './onboarding/BackgroundCheckStep';
import PrepareEnrichStep from './onboarding/PrepareEnrichStep';
import SpouseInterviewStep from './onboarding/SpouseInterviewStep';
import SpiritualHealthStep from './onboarding/SpiritualHealthStep';
import MinistryTransitionsStep from './onboarding/MinistryTransitionsStep';
import CompletionStep from './onboarding/CompletionStep';
import Toast from './Toast';
import Header from './Header';

interface OnboardingStatus {
  hasProfile: boolean;
  currentStep: number;
  onboardingCompleted: boolean;
  steps?: {
    profile: boolean;
    whyChurchPlanting: boolean;
    theology: boolean;
    references: boolean;
    sendingChurch: boolean;
    backgroundCheck: boolean;
    prepareEnrich: boolean;
    spouseInterview: boolean;
    spiritualHealth: boolean;
    ministryTransitions: boolean;
  };
  portalCandidate?: {
    id: number;
    name: string;
    portalStatus: string;
  };
}

interface OnboardingWizardProps {
  onComplete?: () => void;
}

interface OnboardingData {
  profile: any;
  whyChurchPlanting: any;
  theology: any;
  references: any[];
  sendingChurch: any;
  backgroundCheck: any;
  prepareEnrich: any;
  spouseInterview: any;
  spiritualHealth: any;
  ministryTransitions: any;
}

interface StepConfig {
  id: number;
  title: string;
  description: string;
  key: 'profile' | 'whyChurchPlanting' | 'theology' | 'references' | 'sendingChurch' | 'backgroundCheck' | 'prepareEnrich' | 'spouseInterview' | 'spiritualHealth' | 'ministryTransitions';
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [currentSubsection, setCurrentSubsection] = useState<number | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    profile: null,
    whyChurchPlanting: null,
    theology: null,
    references: [],
    sendingChurch: null,
    backgroundCheck: null,
    prepareEnrich: null,
    spouseInterview: null,
    spiritualHealth: null,
    ministryTransitions: null,
  });
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [subsectionCompletions, setSubsectionCompletions] = useState<boolean[]>([]);
  const [referenceTypes, setReferenceTypes] = useState<string[]>(['Pastor/Minister', 'Employer/Supervisor', 'Friend']);
  const [spiritualHealthCompletions, setSpiritualHealthCompletions] = useState<{
    candidate: boolean[];
    spouse: boolean[];
    isMarried: boolean;
  }>({ candidate: [], spouse: [], isMarried: false });

  const steps: StepConfig[] = [
    { id: 1, title: 'Create Profile', description: 'Tell us about yourself and your family', key: 'profile' },
    { id: 2, title: 'Why Church Planting', description: 'Share your calling and vision', key: 'whyChurchPlanting' },
    { id: 3, title: 'References', description: 'Provide three references', key: 'references' },
    { id: 4, title: 'Sending Church', description: 'Tell us about your sending church', key: 'sendingChurch' },
    { id: 5, title: 'Background Check', description: 'Authorization for background screening', key: 'backgroundCheck' },
    { id: 6, title: 'Prepare and Enrich', description: 'Marriage counseling for couples', key: 'prepareEnrich' },
    { id: 7, title: 'Spouse Interview', description: 'Spouse assessment interview', key: 'spouseInterview' },
    { id: 8, title: 'Theological Survey', description: 'Share your theological convictions', key: 'theology' },
    { id: 9, title: 'Spiritual Health', description: 'Spiritual and emotional health assessment', key: 'spiritualHealth' },
    { id: 10, title: 'Ministry Transitions', description: 'Previous ministry staff history', key: 'ministryTransitions' },
    { id: 11, title: 'Complete', description: 'Review and complete your application', key: 'profile' } // Using profile key as placeholder
  ];

  const totalSteps = steps.length;

  // Load existing onboarding data on component mount
  useEffect(() => {
    loadOnboardingData();
  }, []);

  // Listen for subsection changes from step components
  useEffect(() => {
    const handleSubsectionChanged = (event: CustomEvent) => {
      setCurrentSubsection(event.detail);
    };

    window.addEventListener('subsectionChanged' as any, handleSubsectionChanged as EventListener);
    return () => {
      window.removeEventListener('subsectionChanged' as any, handleSubsectionChanged as EventListener);
    };
  }, []);

  // Listen for subsection completion status changes
  useEffect(() => {
    const handleSubsectionCompletionChanged = (event: CustomEvent) => {
      // Check if this is from Spiritual Health (has candidate/spouse) or Theology (has completions array)
      if (event.detail.candidate !== undefined && event.detail.spouse !== undefined) {
        // Spiritual Health section
        setSpiritualHealthCompletions({
          candidate: event.detail.candidate || [],
          spouse: event.detail.spouse || [],
          isMarried: event.detail.isMarried || false
        });
      } else {
        // Theology or References section (has completions array)
        setSubsectionCompletions(event.detail.completions || []);
        // If it's references, also update the reference types
        if (event.detail.referenceTypes) {
          setReferenceTypes(event.detail.referenceTypes);
        }
      }
    };

    window.addEventListener('subsectionCompletionChanged' as any, handleSubsectionCompletionChanged as EventListener);
    return () => {
      window.removeEventListener('subsectionCompletionChanged' as any, handleSubsectionCompletionChanged as EventListener);
    };
  }, []);

  // Reset subsection and completions when changing main steps
  useEffect(() => {
    setCurrentSubsection(null);
    setSubsectionCompletions([]);
    setSpiritualHealthCompletions({ candidate: [], spouse: [], isMarried: false });
  }, [currentStep]);

  const loadOnboardingData = async () => {
    try {
      setIsLoading(true);
      const status = await portalCandidateService.getOnboardingStatus();
      
      // Load existing data for each step
      const newData: OnboardingData = {
        profile: null,
        whyChurchPlanting: null,
        theology: null,
        references: [],
        sendingChurch: null,
        backgroundCheck: null,
        prepareEnrich: null,
        spouseInterview: null,
        spiritualHealth: null,
        ministryTransitions: null,
      };

      // Track which steps have actual data
      const stepsStatus = {
        profile: false,
        whyChurchPlanting: false,
        theology: false,
        references: false,
        sendingChurch: false,
        backgroundCheck: false,
        prepareEnrich: false,
        spouseInterview: false,
        spiritualHealth: false,
        ministryTransitions: false,
      };

      // Load profile data if it exists
      if (status.hasProfile) {
        try {
          const profileResponse = await portalCandidateService.getProfile();
          if (profileResponse.portalCandidate) {
            newData.profile = profileResponse.portalCandidate;
            stepsStatus.profile = true;
          }
        } catch (error) {
          console.error('Error loading profile data:', error);
        }
      }

      // Load why church planting data
      try {
        const whyChurchPlantingResponse = await portalCandidateService.getWhyChurchPlanting();
        if (whyChurchPlantingResponse.whyChurchPlanting) {
          newData.whyChurchPlanting = whyChurchPlantingResponse.whyChurchPlanting;
          stepsStatus.whyChurchPlanting = whyChurchPlantingResponse.whyChurchPlanting.completed || false;
        }
      } catch (error) {
        console.debug('No why church planting data found:', error);
      }

      // Load theology data from the hub portal's TheologicalSurvey table
      try {
        const theologyResponse = await getTheologicalSurvey();
        if (theologyResponse.survey) {
          newData.theology = theologyResponse.survey;
          stepsStatus.theology = true;
        }
      } catch (error) {
        // Survey not found is expected if not completed yet
        console.debug('No theology survey found:', error);
      }

      // Load references data from the hub portal's Reference table
      try {
        const referencesResponse = await getReferences();
        if (referencesResponse.references && referencesResponse.references.length > 0) {
          newData.references = referencesResponse.references;
          stepsStatus.references = true;
        }
      } catch (error) {
        console.debug('No references found:', error);
      }

      // Load sending church data from the hub portal's SendingChurch table
      try {
        const sendingChurchResponse = await getSendingChurch();
        if (sendingChurchResponse.sendingChurch) {
          newData.sendingChurch = sendingChurchResponse.sendingChurch;
          stepsStatus.sendingChurch = true;
        }
      } catch (error) {
        console.debug('No sending church found:', error);
      }

      // Load prepare/enrich data from the backend
      try {
        const prepareEnrichResponse = await portalCandidateService.getPrepareEnrich();
        if (prepareEnrichResponse.prepareEnrich) {
          newData.prepareEnrich = prepareEnrichResponse.prepareEnrich;
          // Mark as complete if requested (married) or skipped (single)
          stepsStatus.prepareEnrich = prepareEnrichResponse.prepareEnrich.requested || prepareEnrichResponse.prepareEnrich.skipped || false;
        }
      } catch (error) {
        console.debug('No prepare/enrich data found:', error);
      }

      // Load spouse interview data
      try {
        const spouseInterviewResponse = await portalCandidateService.getSpouseInterview();
        if (spouseInterviewResponse.spouseInterview) {
          newData.spouseInterview = spouseInterviewResponse.spouseInterview;
          stepsStatus.spouseInterview = spouseInterviewResponse.spouseInterview.submitted || spouseInterviewResponse.spouseInterview.skipped || false;
        }
      } catch (error) {
        console.debug('No spouse interview data found:', error);
      }

      // Load spiritual health data from the backend
      try {
        const spiritualHealthResponse = await portalCandidateService.getSpiritualHealthSurveys();
        if (spiritualHealthResponse) {
          newData.spiritualHealth = spiritualHealthResponse;
          // Check if all required surveys are completed
          const isMarried = newData.profile?.maritalStatus?.toLowerCase() === 'married';
          stepsStatus.spiritualHealth = isMarried 
            ? (spiritualHealthResponse.spiritualHealthCandidateSubmitted && spiritualHealthResponse.spiritualHealthSpouseSubmitted)
            : spiritualHealthResponse.spiritualHealthCandidateSubmitted;
        }
      } catch (error) {
        console.debug('No spiritual health data found:', error);
      }

      // Load background check data from the backend
      try {
        const backgroundCheckResponse = await portalCandidateService.getBackgroundCheck();
        if (backgroundCheckResponse.backgroundCheck) {
          newData.backgroundCheck = backgroundCheckResponse.backgroundCheck;
          stepsStatus.backgroundCheck = backgroundCheckResponse.backgroundCheck.submitted || false;
        }
      } catch (error) {
        console.debug('No background check data found:', error);
      }

      // Load ministry transitions data
      try {
        const ministryTransitionsResponse = await portalCandidateService.getMinistryTransitions();
        if (ministryTransitionsResponse.ministryTransitions) {
          newData.ministryTransitions = ministryTransitionsResponse.ministryTransitions;
          stepsStatus.ministryTransitions = ministryTransitionsResponse.ministryTransitions.completed || false;
        }
      } catch (error) {
        console.debug('No ministry transitions data found:', error);
      }

      // Update status with actual step completion
      setOnboardingStatus({
        ...status,
        steps: stepsStatus
      });

      // Update the onboarding data state
      setOnboardingData(newData);
      
      // Debug logging
      console.log('Onboarding Status Loaded:', {
        status,
        stepsStatus,
        hasData: newData
      });
      
      // Only set to completion step if onboarding is fully completed
      if (status.onboardingCompleted) {
        setCurrentStep(11); // Completion step
      }
      // Otherwise, keep the current step as is (user can navigate freely)
    } catch (error) {
      console.error('Error loading onboarding data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate completion percentage using unified utility
  const getCompletionPercentage = () => {
    if (!onboardingStatus || !onboardingStatus.steps) return 0;
    
    // Use the unified progress calculation utility
    const { calculateProgressFromSteps } = require('../utils/progressCalculation');
    const profileData = onboardingData.profile;
    return calculateProgressFromSteps(onboardingStatus.steps, profileData);
  };

  // Check if a step is completed
  const isStepCompleted = (stepId: number) => {
    if (!onboardingStatus || !onboardingStatus.steps || stepId === 11) return false; // Completion step is never "completed"
    const step = steps.find(s => s.id === stepId);
    if (!step) return false;
    
    // Step 5: Background Check - check if user has submitted
    if (stepId === 5) {
      return onboardingStatus.steps.backgroundCheck || false;
    }
    
    // Step 6: Prepare/Enrich - check if user has visited and skipped (for single) or completed (for married)
    if (stepId === 6) {
      const profileData = onboardingData.profile;
      const prepareEnrichData = onboardingData.prepareEnrich;
      if (profileData?.maritalStatus !== 'married') {
        return prepareEnrichData?.skipped === true;
      }
      return prepareEnrichData?.requested === true || prepareEnrichData?.status === 'requested';
    }

    // Step 7: Spouse Interview - check if submitted (married) or skipped (single)
    if (stepId === 7) {
      const profileData = onboardingData.profile;
      const spouseInterviewData = onboardingData.spouseInterview;
      if (profileData?.maritalStatus !== 'married') {
        return spouseInterviewData?.skipped === true;
      }
      return spouseInterviewData?.submitted === true;
    }
    
    // Step 9: Spiritual Health
    if (stepId === 9) {
      return onboardingStatus.steps.spiritualHealth || false;
    }
    
    return onboardingStatus.steps[step.key];
  };

  // Check if all required steps are completed
  const canComplete = () => {
    if (!onboardingStatus || !onboardingStatus.steps) return false;
    
    // Check all 10 steps for completion, considering admin-side statuses
    for (let i = 1; i <= 10; i++) {
      if (!isStepCompleted(i)) {
        return false;
      }
    }
    
    return true;
  };

  const handleStepComplete = async (stepNumber: number, data: any) => {
    const newData = { ...onboardingData };
    
    switch (stepNumber) {
      case 1:
        newData.profile = data;
        break;
      case 2:
        newData.whyChurchPlanting = data;
        break;
      case 3:
        newData.references = data;
        break;
      case 4:
        newData.sendingChurch = data;
        break;
      case 5:
        newData.backgroundCheck = data;
        break;
      case 6:
        newData.prepareEnrich = data;
        break;
      case 7:
        newData.spouseInterview = data;
        break;
      case 8:
        newData.theology = data;
        break;
      case 9:
        newData.spiritualHealth = data;
        break;
      case 10:
        newData.ministryTransitions = data;
        break;
    }
    
    setOnboardingData(newData);

    // Update the step completion status in real-time so sidebar checkmarks update immediately
    const stepKeyMap: Record<number, string> = {
      1: 'profile', 2: 'whyChurchPlanting', 3: 'references', 4: 'sendingChurch',
      5: 'backgroundCheck', 6: 'prepareEnrich', 7: 'spouseInterview', 8: 'theology', 9: 'spiritualHealth',
      10: 'ministryTransitions'
    };
    const stepKey = stepKeyMap[stepNumber] as keyof NonNullable<OnboardingStatus['steps']>;
    if (stepKey && onboardingStatus) {
      setOnboardingStatus(prev => {
        if (!prev) return prev;
        const updatedSteps = { ...prev.steps!, [stepKey]: true } as NonNullable<OnboardingStatus['steps']>;
        return { ...prev, steps: updatedSteps };
      });
    }
    
    try {
      // Save data to backend
      await saveStepData(stepNumber, data);
      
      // Show success message
      const stepNames = ['Profile', 'Why Church Planting', 'References', 'Sending Church', 'Background Check', 'Prepare and Enrich', 'Spouse Interview', 'Theological Survey', 'Spiritual Health', 'Ministry Transitions'];
      setToast({ 
        message: `${stepNames[stepNumber - 1]} saved successfully!`, 
        type: 'success' 
      });
      
      // Auto-navigate to next step after a short delay
      setTimeout(() => {
        if (stepNumber < totalSteps - 1) {
          // Move to next step if not the last step
          setCurrentStep(stepNumber + 1);
        } else if (stepNumber === 10 && canComplete()) {
          // If all steps complete, go to completion step
          setCurrentStep(11);
        }
      }, 1500); // Wait 1.5 seconds to show the success message
      
    } catch (error) {
      // Show error message
      setToast({ 
        message: 'Error saving data. Please try again.', 
        type: 'error' 
      });
      throw error;
    }
  };

  // Navigation methods
  const navigateToStep = (stepId: number) => {
    setCurrentStep(stepId);
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const saveStepData = async (stepNumber: number, data: any) => {
    try {
      switch (stepNumber) {
        case 1:
          // Profile step - handled by ProfileStep component itself
          break;
        case 2:
          // Why Church Planting - save to database
          await portalCandidateService.submitWhyChurchPlanting(data);
          break;
        case 3:
          // References - save to hub portal's Reference table
          await submitReferences(data);
          break;
        case 4:
          // Sending church - save to hub portal's SendingChurch table
          await submitSendingChurch(data);
          break;
        case 5:
          // Background check - save to database and send email
          await portalCandidateService.submitBackgroundCheck(data);
          break;
        case 6:
          // Prepare/Enrich - save to database and send email
          await portalCandidateService.requestPrepareEnrich(data);
          break;
        case 7:
          // Spouse Interview - save to database
          await portalCandidateService.submitSpouseInterview(data);
          break;
        case 8:
          // Theological survey - save to hub portal's TheologicalSurvey table
          await submitTheologicalSurvey(data);
          break;
        case 9:
          // Spiritual Health - handled by SpiritualHealthStep component itself via API
          break;
        case 10:
          // Ministry Transitions - save to database
          await portalCandidateService.submitMinistryTransitions(data);
          break;
        default:
          console.warn(`Unknown step number: ${stepNumber}`);
      }
      
      // Reload onboarding status after saving
      await loadOnboardingData();
    } catch (error) {
      console.error(`Error saving step ${stepNumber} data:`, error);
      throw error;
    }
  };

  const handleOnboardingComplete = async () => {
    try {
      setIsLoading(true);
      // TODO: Implement API call to complete onboarding
      // await api.post('/onboarding/complete');
      
      if (onComplete) {
        onComplete();
      } else {
        // Redirect to dashboard
        window.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ProfileStep
            data={onboardingData.profile}
            onComplete={(data) => handleStepComplete(1, data)}
            onNext={handleNextStep}
          />
        );
      case 2:
        return (
          <WhyChurchPlantingStep
            data={onboardingData.whyChurchPlanting}
            onComplete={(data) => handleStepComplete(2, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 3:
        return (
          <ReferencesStep
            data={onboardingData.references}
            onComplete={(data) => handleStepComplete(3, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 4:
        return (
          <SendingChurchStep
            data={onboardingData.sendingChurch}
            onComplete={(data) => handleStepComplete(4, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 5:
        return (
          <BackgroundCheckStep
            data={onboardingData.backgroundCheck}
            profileData={onboardingData.profile}
            onComplete={(data) => handleStepComplete(5, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 6:
        return (
          <PrepareEnrichStep
            data={onboardingData.prepareEnrich}
            profileData={onboardingData.profile}
            onComplete={(data) => handleStepComplete(6, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 7:
        return (
          <SpouseInterviewStep
            data={onboardingData.spouseInterview}
            profileData={onboardingData.profile}
            onComplete={(data) => handleStepComplete(7, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 8:
        return (
          <TheologyStep
            data={onboardingData.theology}
            onComplete={(data) => handleStepComplete(8, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 9:
        return (
          <SpiritualHealthStep
            data={onboardingData.spiritualHealth}
            profileData={onboardingData.profile}
            onComplete={(data) => handleStepComplete(9, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 10:
        return (
          <MinistryTransitionsStep
            data={onboardingData.ministryTransitions}
            onComplete={(data) => handleStepComplete(10, data)}
            onNext={handleNextStep}
            onPrevious={handlePreviousStep}
          />
        );
      case 11:
        if (!canComplete()) {
          return (
            <div className="p-8 text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Complete All Steps First</h3>
              <p className="text-gray-600 mb-6">
                Please complete all the required steps before finishing your onboarding.
              </p>
              <div className="space-y-2">
                {steps.slice(0, 10).map((step) => (
                  <div key={step.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm text-gray-700">{step.title}</span>
                    <span className={`text-sm ${isStepCompleted(step.id) ? 'text-green-600' : 'text-red-600'}`}>
                      {isStepCompleted(step.id) ? '✓ Complete' : '○ Incomplete'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <button
                  onClick={() => {
                    // Navigate to first incomplete step
                    const firstIncomplete = steps.slice(0, 10).find(step => !isStepCompleted(step.id));
                    if (firstIncomplete) {
                      navigateToStep(firstIncomplete.id);
                    }
                  }}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Continue Onboarding
                </button>
              </div>
            </div>
          );
        }
        return (
          <CompletionStep
            data={onboardingData}
            onComplete={() => handleOnboardingComplete()}
            onPrevious={handlePreviousStep}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading onboarding data...</p>
        </div>
      </div>
    );
   }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 sm:px-6 lg:flex-row lg:px-8">
        <aside className="lg:w-80">
          <div className="rounded-lg bg-white p-6 shadow">
            <div className="mb-6">
              <img
                className="h-16 w-auto mx-auto mb-4"
                src="/logo.png"
                alt="242Network"
              />
              <h1 className="text-2xl font-bold text-gray-900">Welcome to 242Network</h1>
              <p className="mt-1 text-sm text-gray-600">
                Let's get you set up for your church planting journey.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-gray-500">
                <span>Overall Progress</span>
                <span>{getCompletionPercentage()}%</span>
              </div>
              <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    getCompletionPercentage() === 100 ? 'bg-green-600' : 'bg-primary-600'
                  }`}
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
              </div>
            </div>

            {canComplete() && currentStep !== 10 && (
              <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                <p className="text-sm text-green-800">
                  🎉 All steps completed! You can now{' '}
                  <button
                    type="button"
                    onClick={() => navigateToStep(10)}
                    className="font-medium underline hover:no-underline"
                  >
                    complete your onboarding
                  </button>
                  .
                </p>
              </div>
            )}

            <nav className="mt-6 space-y-2">
              {steps.map((step) => {
                const isCompleted = isStepCompleted(step.id);
                const isCurrent = step.id === currentStep;
                const isLockedCompletion = step.id === 11 && !canComplete();
                const showSubsections = (step.id === 1 || step.id === 8 || step.id === 3 || step.id === 9) && isCurrent;
                const isPrepareEnrichSkipped = step.id === 6 && onboardingData.prepareEnrich?.skipped === true;
                const isSpouseInterviewSkipped = step.id === 7 && onboardingData.spouseInterview?.skipped === true;

                return (
                  <div key={step.id}>
                    <button
                      type="button"
                      onClick={() => (!isLockedCompletion ? navigateToStep(step.id) : null)}
                      disabled={isLockedCompletion}
                      className={`w-full rounded-lg border px-4 py-3 text-left transition-colors ${
                        isCurrent
                          ? 'border-primary-600 bg-primary-50 text-primary-700'
                          : isCompleted
                          ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                          : isLockedCompletion
                          ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-primary-200 hover:bg-primary-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className={`flex h-8 w-8 items-center justify-center rounded-full border text-sm font-medium ${
                            isCompleted
                              ? 'border-green-600 bg-green-600 text-white'
                              : isCurrent
                              ? 'border-primary-600 bg-primary-100 text-primary-600'
                              : 'border-gray-300 bg-gray-100 text-gray-600'
                          }`}
                        >
                          {isCompleted ? (
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : (
                            step.id
                          )}
                        </span>
                        <span className="flex flex-col flex-1">
                          <span className="flex items-center gap-2 text-sm font-medium">
                            {step.title}
                            {(isPrepareEnrichSkipped || isSpouseInterviewSkipped) && (
                              <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
                                Skipped
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-gray-500">{step.description}</span>
                        </span>
                      </div>
                    </button>
                    
                    {/* Show subsections for Profile, Theology, References, and Spiritual Health when current */}
                    {showSubsections && (
                      <div className="mt-2 ml-4 space-y-1 border-l-2 border-primary-200 pl-4">
                        {step.id === 1 && (
                          <>
                            {[
                              'Personal Information',
                              'Ministry Information',
                              'Media'
                            ].map((sectionTitle, index) => {
                              const isComplete = subsectionCompletions[index] || false;
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setCurrentSubsection(index);
                                    // Pass section navigation to ProfileStep
                                    const event = new CustomEvent('navigateToSection', { detail: index });
                                    window.dispatchEvent(event);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs rounded transition-colors flex items-center justify-between ${
                                    currentSubsection === index
                                      ? 'bg-primary-100 text-primary-700 font-medium'
                                      : 'hover:bg-primary-50 text-gray-600 hover:text-primary-700'
                                  }`}
                                >
                                  <span>{sectionTitle}</span>
                                  {isComplete && (
                                    <svg className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </>
                        )}
                        {step.id === 8 && (
                          <>
                            {[
                              'Scripture',
                              'God & the Trinity',
                              'Humanity & Sin',
                              'Christology',
                              'Salvation',
                              'Ecclesiology',
                              'Eschatology',
                              'Baptist Distinctives',
                              'Ministry Philosophy & Practice',
                              'Missional & Cultural Engagement',
                              'Personal & Family Life'
                            ].map((sectionTitle, index) => {
                              const isComplete = subsectionCompletions[index] || false;
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setCurrentSubsection(index);
                                    // Pass section navigation to TheologyStep
                                    const event = new CustomEvent('navigateToSection', { detail: index });
                                    window.dispatchEvent(event);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs rounded transition-colors flex items-center justify-between ${
                                    currentSubsection === index
                                      ? 'bg-primary-100 text-primary-700 font-medium'
                                      : 'hover:bg-primary-50 text-gray-600 hover:text-primary-700'
                                  }`}
                                >
                                  <span>{sectionTitle}</span>
                                  {isComplete && (
                                    <svg className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </>
                        )}
                        {step.id === 3 && (
                          <>
                            {referenceTypes.map((referenceTitle, index) => {
                              const isComplete = subsectionCompletions[index] || false;
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setCurrentSubsection(index);
                                    // Pass section navigation to ReferencesStep
                                    const event = new CustomEvent('navigateToSection', { detail: index });
                                    window.dispatchEvent(event);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs rounded transition-colors flex items-center justify-between ${
                                    currentSubsection === index
                                      ? 'bg-primary-100 text-primary-700 font-medium'
                                      : 'hover:bg-primary-50 text-gray-600 hover:text-primary-700'
                                  }`}
                                >
                                  <span>Reference {index + 1}: {referenceTitle}</span>
                                  {isComplete && (
                                    <svg className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                              );
                            })}
                          </>
                        )}
                        {step.id === 9 && (
                          <>
                            {[
                              'Spiritual Health',
                              'Emotional Health',
                              'Calling and Resilience',
                              'Relational Health',
                              'Spiritual Warfare Health',
                              'Accountability Health'
                            ].map((sectionTitle, index) => {
                              const candidateComplete = spiritualHealthCompletions.candidate[index] || false;
                              const spouseComplete = spiritualHealthCompletions.spouse[index] || false;
                              const showTwoColumn = spiritualHealthCompletions.isMarried;
                              
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setCurrentSubsection(index);
                                    // Pass section navigation to SpiritualHealthStep
                                    const event = new CustomEvent('navigateToSection', { detail: index });
                                    window.dispatchEvent(event);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-xs rounded transition-colors ${
                                    currentSubsection === index
                                      ? 'bg-primary-100 text-primary-700 font-medium'
                                      : 'hover:bg-primary-50 text-gray-600 hover:text-primary-700'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="flex-1">{sectionTitle}</span>
                                    {showTwoColumn ? (
                                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        <div className="flex flex-col items-center gap-0.5">
                                          <span className="text-[9px] text-gray-500 uppercase">Can</span>
                                          {candidateComplete && (
                                            <svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                        </div>
                                        <div className="flex flex-col items-center gap-0.5">
                                          <span className="text-[9px] text-gray-500 uppercase">Spo</span>
                                          {spouseComplete && (
                                            <svg className="h-3.5 w-3.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      candidateComplete && (
                                        <svg className="h-4 w-4 text-green-600 flex-shrink-0 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="flex-1">
          <div className="overflow-hidden rounded-lg bg-white shadow">
            {renderStep()}
          </div>
        </main>
        </div>
      </div>
    </>
  );
};

export default OnboardingWizard;
